import Resizable from '@corvu/resizable';
import { createMemo, Index, JSX } from 'solid-js';
import { reconcile, unwrap } from 'solid-js/store';
import {
  PaneElementProps,
  PaneManagerViewerProps,
} from './models/pane-manager-models';
import { createPaneSync } from './createPaneSync';

export const PaneManager = <T, TContentConfig>(
  props: PaneManagerViewerProps<T, TContentConfig>
): JSX.Element => {
  const registerSyncedPane = createPaneSync();

  const activeLayout = createMemo(() => {
    const activeLayout = props.layouts.layouts.find(
      (layout) => layout.label === props.layouts.activeLayoutLabel
    );
    return activeLayout ?? props.layouts.layouts[0];
  });

  const activeRows = createMemo(() => {
    return activeLayout().paneRows;
  });

  return (
    <div class="size-full">
      <Resizable
        class="size-full"
        orientation="vertical"
        sizes={activeRows().map((row) => row.config.size)}
        onSizesChange={(s) => {
          if (s.length < 1 || activeRows().length != s.length) {
            // This happens in tests for some reason.
            return;
          }
          const rows = activeRows().map((row) => ({
            ...row,
            config: {
              ...row.config,
              size: s.shift()!,
            },
          }));

          const activeLabel = unwrap(activeLayout().label);
          props.setLayouts(
            'layouts',
            (layouts) => {
              return layouts.label === activeLabel;
            },
            'paneRows',
            reconcile(rows)
          );
        }}
      >
        <Index each={activeRows()}>
          {(paneRow, rowInd): JSX.Element => {
            return (
              <>
                <Resizable.Panel
                  initialSize={activeRows()[rowInd].config.size}
                  minSize={paneRow().config.minSize}
                  class="rounded-sm shadow-sm overflow-hidden"
                >
                  <Resizable
                    orientation="horizontal"
                    class="size-full"
                    sizes={
                      // TODO: avoid warning: "computations created outside a `createRoot` or `render` will never be disposed"
                      activeRows()[rowInd]
                        ? activeRows()[rowInd].cells.map((cell) => {
                            return cell.size;
                          })
                        : []
                    }
                    onSizesChange={(s) => {
                      const rowCells = activeRows()[rowInd]?.cells;
                      if (s.length < 1 || rowCells.length !== s.length) {
                        // For some reason, this happens in tests and some edge cases.
                        return;
                      }

                      const cells = rowCells.map((cell) => ({
                        ...cell,
                        size: s.shift()!,
                      }));

                      const activeLabel = unwrap(activeLayout().label);
                      props.setLayouts(
                        'layouts',
                        (layouts) => {
                          return layouts.label === activeLabel;
                        },
                        'paneRows',
                        rowInd,
                        'cells',
                        reconcile(cells)
                      );
                    }}
                  >
                    <Index each={paneRow().cells}>
                      {(paneCell, cellInd): JSX.Element => (
                        <>
                          <Resizable.Panel
                            data-testid={`pane`}
                            initialSize={paneCell().size}
                            minSize={paneCell().minSize}
                            class="rounded-sm shadow-sm overflow-hidden"
                          >
                            <PaneWrapper<T, TContentConfig>
                              paneElement={props.paneElement}
                              data={props.data}
                              allowPopup={props.allowPopup}
                              web3DPath={props.web3DPath}
                              registerSyncedPane={registerSyncedPane}
                              layoutConfig={{
                                rowIdx: rowInd,
                                cellIdx: cellInd,
                                layouts: props.layouts,
                                setLayouts: props.setLayouts,
                              }}
                            />
                          </Resizable.Panel>
                          {cellInd < paneRow().cells.length - 1 && (
                            <Resizable.Handle
                              aria-label="Resize Handle"
                              class="group basis-2 py-[3px] text-black"
                            >
                              <div class="size-full rounded-sm transition-colors corvu-group-active:bg-current corvu-group-dragging:bg-current" />
                            </Resizable.Handle>
                          )}
                        </>
                      )}
                    </Index>
                  </Resizable>
                </Resizable.Panel>
                {rowInd < activeRows().length - 1 && (
                  <Resizable.Handle
                    aria-label="Resize Handle"
                    class="group basis-2 px-[3px] text-black"
                  >
                    <div class="size-full rounded-sm transition-colors corvu-group-active:bg-current corvu-group-dragging:bg-current" />
                  </Resizable.Handle>
                )}
              </>
            );
          }}
        </Index>
      </Resizable>
    </div>
  );
};

// This wrapper is needed for SolidJS to optimise component loading (avoid the reload).
const PaneWrapper = <T, TContentConfig>(
  props: PaneElementProps<T, TContentConfig> & {
    paneElement(props: PaneElementProps<T, TContentConfig>): JSX.Element;
  }
): JSX.Element => {
  const unwrapped = unwrap(props);
  return unwrapped.paneElement({
    data: unwrapped.data,
    registerSyncedPane: unwrapped.registerSyncedPane,
    web3DPath: unwrapped.web3DPath,
    allowPopup: unwrapped.allowPopup,
    layoutConfig: unwrapped.layoutConfig,
  });
};
